/**
 * VCloud data transformer
 */
import { ServiceStatus } from '@/modules/Service/types'
import type { VCloudRow, TransformVCloudDataOptions } from './vcloud-types'
import {
    parseVMConfig,
    isStorageType,
    isPublicIPType,
    isFloatingIPType,
    isBandwidthType,
} from './vcloud-helpers'

export const transformVCloudData = ({
    service,
    data,
    t,
}: TransformVCloudDataOptions): VCloudRow[] => {
    if (!data) return []

    // -------------------------------------------------------------------------
    // Step 1: Build service groups (main service + addon services)
    // -------------------------------------------------------------------------
    interface ServiceGroup {
        serviceId: string
        serviceName: string
        serviceStatus: string
        isMain: boolean
        items: any[]
        employee_names?: string[]
        service_start_date?: string
        current_startdate?: string
        current_enddate?: string
        continue_startdate?: string
        continue_enddate?: string
        service_tax_percentage?: number
        service_price_register?: number
        service_pricing_model?: string
    }

    const serviceGroups: Map<string, ServiceGroup> = new Map()

    // Main service group
    const mainServiceId = service?.service_id || 'main'
    serviceGroups.set(mainServiceId, {
        serviceId: mainServiceId,
        serviceName:
            service?.service_name ||
            t('main_service', { defaultValue: 'Dịch vụ chính' }),
        serviceStatus: service?.service_status,
        isMain: true,
        items: [],
        employee_names: service?.employee_names,
        service_start_date: service?.service_start_date,
        current_startdate: service?.current_startdate,
        current_enddate: service?.current_enddate,
        continue_startdate: service?.continue_startdate,
        continue_enddate: service?.continue_enddate,
        service_tax_percentage: service?.service_tax_percentage,
        service_price_register: service?.service_price_register,
        service_pricing_model: service?.service_pricing_model,
    })

    // Add main service config options
    const mainConfigs = (data.configoptions || []).filter(
        (item: any) =>
            item.commitment_value !== null &&
            item.commitment_value !== undefined &&
            item.commitment_value > 0
    )
    serviceGroups.get(mainServiceId)!.items = mainConfigs.map((item: any) => ({
        ...item,
        isAddon: false,
    }))

    // Addon service groups
    if (data.addons && data.addons.length > 0) {
        data.addons.forEach((addon: any) => {
            const addonConfigs = (addon.configoptions || []).filter(
                (item: any) =>
                    item.commitment_value !== null &&
                    item.commitment_value !== undefined &&
                    item.commitment_value > 0
            )
            if (addonConfigs.length > 0) {
                serviceGroups.set(addon.service_id, {
                    serviceId: addon.service_id,
                    serviceName:
                        addon.service_name ||
                        t('addon_service', { defaultValue: 'Addon' }),
                    serviceStatus: addon.service_status,
                    isMain: false,
                    items: addonConfigs.map((item: any) => ({
                        ...item,
                        isAddon: true,
                        addon_service_id: addon.service_id,
                        addon_service_name: addon.service_name,
                        addon_service_status: addon.service_status,
                    })),
                    employee_names: service?.employee_names,
                    service_start_date:
                        addon.service_start_date || service?.service_start_date,
                    current_startdate:
                        addon.current_startdate || service?.current_startdate,
                    current_enddate:
                        addon.current_enddate || service?.current_enddate,
                    continue_startdate:
                        addon.continue_startdate || service?.continue_startdate,
                    continue_enddate:
                        addon.continue_enddate || service?.continue_enddate,
                    service_tax_percentage: service?.service_tax_percentage,
                    service_price_register: addon.service_price_register,
                    service_pricing_model: addon.service_pricing_model,
                })
            }
        })
    }

    // If no config options at all, return empty (PAYG)
    const totalItems = Array.from(serviceGroups.values()).reduce(
        (sum, g) => sum + g.items.length,
        0
    )
    if (totalItems === 0) return []

    // -------------------------------------------------------------------------
    // Step 2: Helper functions
    // -------------------------------------------------------------------------
    const getServiceType = (
        item: any
    ): 'vm_instance' | 'vm_lb' | 'other_service' => {
        const type = item.type?.toLowerCase() || ''
        const name = item.name?.toLowerCase() || ''
        if (type === 'vm-instance' || name.includes('instance'))
            return 'vm_instance'
        if (
            type === 'vm-lb' ||
            name.includes('lb') ||
            name.includes('load') ||
            name.includes('balancer')
        )
            return 'vm_lb'
        return 'other_service'
    }

    const calculateTotals = (items: any[]) => {
        return items.reduce(
            (totals, item) => {
                const serviceType = getServiceType(item)
                const quantity = item.commitment_value || 0
                const config = parseVMConfig(item.name || '')

                if (serviceType === 'vm_instance' || serviceType === 'vm_lb') {
                    totals.vCores += config.cores * quantity
                    totals.ram += config.ram * quantity
                } else {
                    if (isStorageType(item.type, item.name)) {
                        totals.storage += quantity
                    }
                    if (isPublicIPType(item.type, item.name)) {
                        totals.publicIP += quantity
                    }
                    if (isFloatingIPType(item.type, item.name)) {
                        totals.floatingIP += quantity
                    }
                    if (isBandwidthType(item.type, item.name)) {
                        totals.bandwidth += quantity
                    }
                }
                const itemPrice =
                    item.commitment_price_total ||
                    (item.commitment_price_per_unit || 0) * quantity
                totals.price += itemPrice
                return totals
            },
            {
                vCores: 0,
                ram: 0,
                storage: 0,
                publicIP: 0,
                floatingIP: 0,
                bandwidth: 0,
                price: 0,
            }
        )
    }

    // -------------------------------------------------------------------------
    // Step 3: Build result rows
    // -------------------------------------------------------------------------
    const result: VCloudRow[] = []
    const grandTotals = {
        vCores: 0,
        ram: 0,
        storage: 0,
        publicIP: 0,
        floatingIP: 0,
        bandwidth: 0,
        price: 0,
        priceRegister: 0,
        combinedTotal: 0,
    }

    // Process each service group (main first, then addons)
    const sortedGroups = Array.from(serviceGroups.values()).sort((a, _b) =>
        a.isMain ? -1 : 1
    )

    // Statuses that should NOT be included in grand total
    const excludedStatuses = [
        ServiceStatus.Cancelled,
        ServiceStatus.Rejected,
        ServiceStatus.PendingTerminated,
    ]
    const mainServiceExcluded = excludedStatuses.includes(
        service?.service_status as ServiceStatus
    )

    sortedGroups.forEach((group) => {
        if (group.items.length === 0) return

        const serviceTotals = calculateTotals(group.items)

        const shouldIncludeInTotal =
            mainServiceExcluded ||
            !excludedStatuses.includes(group.serviceStatus as ServiceStatus)
        if (shouldIncludeInTotal) {
            grandTotals.vCores += serviceTotals.vCores
            grandTotals.ram += serviceTotals.ram
            grandTotals.storage += serviceTotals.storage
            grandTotals.publicIP += serviceTotals.publicIP
            grandTotals.floatingIP += serviceTotals.floatingIP
            grandTotals.bandwidth += serviceTotals.bandwidth
            grandTotals.price += serviceTotals.price
            grandTotals.priceRegister += group.service_price_register || 0
            if (group.service_pricing_model === 'package') {
                grandTotals.combinedTotal += group.service_price_register || 0
            } else {
                grandTotals.combinedTotal += serviceTotals.price
            }
        }

        // Service header row
        result.push({
            _rowId: `service-${group.serviceId}`,
            _rowType: 'service-header',
            _isMainService: group.isMain,
            service_configoption_id: `service-header-${group.serviceId}`,
            name: group.serviceName,
            totalVCores: serviceTotals.vCores,
            totalRAM: serviceTotals.ram,
            totalStorage: serviceTotals.storage,
            totalPublicIP: serviceTotals.publicIP,
            totalFloatingIP: serviceTotals.floatingIP,
            totalBandwidth: serviceTotals.bandwidth,
            commitment_price_total: serviceTotals.price,
            service_price_register: group.service_price_register,
            service_id: group.serviceId,
            service_status: group.serviceStatus,
            employee_names: group.employee_names,
            service_start_date: group.service_start_date,
            current_startdate: group.current_startdate,
            current_enddate: group.current_enddate,
            continue_startdate: group.continue_startdate,
            continue_enddate: group.continue_enddate,
            service_tax_percentage: group.service_tax_percentage,
            service_pricing_model: group.service_pricing_model,
        })

        // Group items by type within this service
        const typeGroups = {
            vm_instance: [] as any[],
            vm_lb: [] as any[],
            other_service: [] as any[],
        }
        group.items.forEach((item) => {
            const serviceType = getServiceType(item)
            typeGroups[serviceType].push(item)
        })

        // Add type headers and items
        const typeOrder: Array<'vm_instance' | 'vm_lb' | 'other_service'> = [
            'vm_instance',
            'vm_lb',
            'other_service',
        ]
        const typeLabels = {
            vm_instance: t('vm.vm_instance', { defaultValue: 'VM Instance' }),
            vm_lb: t('vm.vm_lb', { defaultValue: 'Load Balancer' }),
            other_service: t('vm.other_service', {
                defaultValue: 'Dịch vụ khác',
            }),
        }

        typeOrder.forEach((typeKey) => {
            const items = typeGroups[typeKey]
            if (items.length === 0) return

            // Calculate type totals
            const typeTotals = items.reduce(
                (totals, item) => {
                    const quantity = item.commitment_value || 0
                    const config = parseVMConfig(item.name || '')
                    if (typeKey === 'vm_instance' || typeKey === 'vm_lb') {
                        totals.vCores += config.cores * quantity
                        totals.ram += config.ram * quantity
                    } else {
                        if (isStorageType(item.type, item.name))
                            totals.storage += quantity
                        if (isPublicIPType(item.type, item.name))
                            totals.publicIP += quantity
                        if (isFloatingIPType(item.type, item.name))
                            totals.floatingIP += quantity
                        if (isBandwidthType(item.type, item.name))
                            totals.bandwidth += quantity
                    }
                    return totals
                },
                {
                    vCores: 0,
                    ram: 0,
                    storage: 0,
                    publicIP: 0,
                    floatingIP: 0,
                    bandwidth: 0,
                }
            )

            // Type header row
            result.push({
                _rowId: `type-${group.serviceId}-${typeKey}`,
                _rowType: 'type-header',
                _serviceType: typeKey,
                _parentServiceId: group.serviceId,
                _isMainService: group.isMain,
                service_configoption_id: `type-header-${group.serviceId}-${typeKey}`,
                name: typeLabels[typeKey],
                totalVCores: typeTotals.vCores,
                totalRAM: typeTotals.ram,
                totalStorage: typeTotals.storage,
                totalPublicIP: typeTotals.publicIP,
                totalFloatingIP: typeTotals.floatingIP,
                totalBandwidth: typeTotals.bandwidth,
                service_pricing_model: group.service_pricing_model,
            })

            // Item rows
            items.forEach((item) => {
                const quantity = item.commitment_value || 0
                const calculatedTotal =
                    item.commitment_price_total ||
                    (item.commitment_price_per_unit || 0) * quantity

                const baseRow: VCloudRow = {
                    _rowId: `item-${item.service_configoption_id}`,
                    _rowType: 'item',
                    _serviceType: typeKey,
                    _parentServiceId: group.serviceId,
                    _isMainService: group.isMain,
                    ...item,
                    commitment_price_total: calculatedTotal,
                    service_tax_percentage: service?.service_tax_percentage,
                    service_id: group.serviceId,
                    service_status: group.serviceStatus,
                    service_pricing_model: group.service_pricing_model,
                }
                // Parse fields for other_service
                if (typeKey === 'other_service') {
                    if (isStorageType(item.type, item.name))
                        baseRow.storage = item.commitment_value || 0
                    if (isPublicIPType(item.type, item.name))
                        baseRow.publicIP = item.commitment_value || 0
                    if (isFloatingIPType(item.type, item.name))
                        baseRow.floatingIP = item.commitment_value || 0
                    if (isBandwidthType(item.type, item.name))
                        baseRow.bandwidth = item.commitment_value || 0
                }
                result.push(baseRow)
            })
        })
    })

    // -------------------------------------------------------------------------
    // Step 4: Grand total summary row
    // -------------------------------------------------------------------------
    result.push({
        _rowId: 'total-summary',
        _rowType: 'summary',
        service_configoption_id: 'total-summary',
        name: t('total-service-price', { defaultValue: 'Tổng cộng' }),
        commitment_price_total: grandTotals.combinedTotal,
        service_price_register: grandTotals.priceRegister,
        totalVCores: grandTotals.vCores,
        totalRAM: grandTotals.ram,
        totalStorage: grandTotals.storage,
        totalPublicIP: grandTotals.publicIP,
        totalFloatingIP: grandTotals.floatingIP,
        totalBandwidth: grandTotals.bandwidth,
        service_tax_percentage: service?.service_tax_percentage,
        service_id: service?.service_id,
        employee_names: service?.employee_names,
        service_start_date: service?.service_start_date,
        service_status: service?.service_status,
        current_startdate: service?.current_startdate,
        current_enddate: service?.current_enddate,
        continue_startdate: service?.continue_startdate,
        continue_enddate: service?.continue_enddate,
    })

    return result
}
